import { useState } from "react";
import { Wand2, ArrowUp, ArrowDown, Sparkles, X, Plus, MessageSquare, Mail } from "lucide-react";
import { Section } from "../dc";
import { TEMPLATES, PRIORITY } from "./data";

const RULES = ["Boarded Nov 20–30, 2025", "Visited since Aug 1", "No booking Nov 24 – Dec 1, 2026", "Has a mobile number · not opted out"];

const PriorityList = ({ order, setOrder }) => {
  const move = (i, d) => { const o = [...order]; [o[i], o[i + d]] = [o[i + d], o[i]]; setOrder(o); };
  const removed = PRIORITY.filter((p) => !order.some((o) => o.k === p.k));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid="builder-priority">
      {order.map((p, i) => (
        <div key={p.k} className="raised" style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8 }} data-testid={`priority-${p.k}`}>
          <span className="mono" style={{ fontWeight: 700, color: "var(--accent-ink)", width: 16 }}>{i + 1}</span>
          <div style={{ flex: 1 }}><b style={{ fontSize: 13 }}>{p.label}</b><div className="muted" style={{ fontSize: 11 }}>{p.sub}</div></div>
          <button className="btn btn-sm" disabled={!i} onClick={() => move(i, -1)} data-testid={`priority-up-${p.k}`}><ArrowUp size={14} /></button>
          <button className="btn btn-sm" disabled={i === order.length - 1} onClick={() => move(i, 1)} data-testid={`priority-down-${p.k}`}><ArrowDown size={14} /></button>
          <button className="btn btn-sm" title="Leave this group out" disabled={order.length === 1} onClick={() => setOrder(order.filter((o) => o.k !== p.k))} data-testid={`priority-remove-${p.k}`}><X size={14} /></button>
        </div>
      ))}
      {removed.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }} data-testid="priority-removed">
          <span className="muted" style={{ fontSize: 11 }}>Left out:</span>
          {removed.map((p) => (
            <button key={p.k} className="chip" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }} title="Add back" onClick={() => setOrder([...order, p])} data-testid={`priority-restore-${p.k}`}>
              <span style={{ textDecoration: "line-through" }}>{p.label}</span><Plus size={11} style={{ marginLeft: 4 }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const Builder = ({ onBuild }) => {
  const [tpl, setTpl] = useState("thanksgiving");
  const [ask, setAsk] = useState("Customers who boarded with us last Thanksgiving and have visited since, but haven't booked this holiday yet");
  const [order, setOrder] = useState(PRIORITY.filter((p) => p.k !== "private_play"));
  const [channel, setChannel] = useState("sms");
  return (
    <Section title="Campaign builder" sub="Start from a template or describe who you want in plain English. AI turns it into rules you can check." icon={Wand2} testid="campaign-builder">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <span className="label">1 · Pick a template</span>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
            {TEMPLATES.map((t) => (
              <button key={t.k} onClick={() => setTpl(t.k)} className="card" style={{ textAlign: "left", padding: 12, cursor: "pointer", borderColor: tpl === t.k ? "var(--accent-ink)" : undefined, boxShadow: tpl === t.k ? "0 0 0 2px rgba(31,138,124,.18)" : undefined }} data-testid={`template-${t.k}`}>
                <b style={{ fontSize: 13 }}>{t.name}</b>
                <div className="muted" style={{ fontSize: 11, margin: "4px 0 6px" }}>{t.desc}</div>
                <span className="mono" style={{ fontSize: 12, fontWeight: 700, color: "var(--accent-ink)" }}>{t.count} owners</span>
              </button>
            ))}
          </div>
          <span className="label" style={{ marginTop: 4 }}>…or describe your audience</span>
          <textarea className="input" rows={3} value={ask} onChange={(e) => setAsk(e.target.value)} style={{ resize: "vertical" }} data-testid="builder-ask" />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} data-testid="builder-rules">
            <Sparkles size={14} color="var(--accent-ink)" />
            {RULES.map((r) => <span key={r} className="chip">{r}</span>)}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <span className="label">2 · Who goes first</span>
          <PriorityList order={order} setOrder={setOrder} />
          <span className="label" style={{ marginTop: 4 }}>3 · Channel</span>
          <div className="tabs" style={{ alignSelf: "flex-start" }} data-testid="builder-channel">
            {[["sms", "Text", MessageSquare], ["email", "Email", Mail]].map(([k, l, I]) => (
              <button key={k} className={`tab ${channel === k ? "active" : ""}`} onClick={() => setChannel(k)} data-testid={`channel-${k}`}><I size={13} style={{ marginRight: 4 }} />{l}</button>
            ))}
          </div>
          <div className="muted" style={{ fontSize: 11 }}>Only owners whose Gingr profile allows {channel === "sms" ? "text messages" : "email"} are included.</div>
          <span className="label" style={{ marginTop: 4 }}>4 · Guardrails</span>
          <div className="muted" style={{ fontSize: 12, lineHeight: 1.6 }} data-testid="builder-guardrails">
            Skip anyone texted in the last 7 days · skip "no marketing" Gingr labels · max <b className="mono">50</b>/day · 8am–8pm only
          </div>
          <button className="btn btn-primary" onClick={() => onBuild(order, channel)} style={{ alignSelf: "flex-start", marginTop: 4 }} data-testid="builder-generate">Generate audience · 86 owners</button>
        </div>
      </div>
    </Section>
  );
};
